import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Task, TaskStatus } from "src/app/features/task/domains/task";
import { TaskCategory } from "src/app/features/task/domains/task-category";
import { TaskCategoryService } from "src/app/features/task/services/task-category.service";
import { TaskService } from "src/app/features/task/services/task.service";
import { Employee } from "src/app/features/employees/domains/employee";
import { EmployeesService } from "src/app/features/employees/services/employees.service";
import { TaskListShellComponent } from "src/app/features/task/task-list-shell/task-list-shell.component";
import { EvaService } from "src/app/core/services/eva.service";
import { ProjectService } from "src/app/features/project/services/project.service";
import { Project } from "src/app/domains/project";
import { SelectOption } from "src/app/shared/ds/eva-select/eva-select.component";
import { EvaCompanyService } from "src/app/features/eva-company/services/evaCompany.service";
import { NoteContextService } from "src/app/services/note-context.service";
import { formatObjectScopeLabel } from "./object-scope-label";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

@Component({
  selector: "app-drawer-tasks",
  templateUrl: "./drawer-tasks.component.html",
  styleUrls: ["./drawer-tasks.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerTasksComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  readonly cdr = inject(ChangeDetectorRef);
  private taskCategoryService = inject(TaskCategoryService);
  private taskService = inject(TaskService);
  private employeesService = inject(EmployeesService);
  private evaService = inject(EvaService);
  private elementRef = inject(ElementRef);
  private projectService = inject(ProjectService);
  private noteContextService = inject(NoteContextService);
  private evaCompanyService = inject(EvaCompanyService);

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Groupes de types backend par chip */
  private readonly CHIP_GROUPS: Record<string, string[]> = {
    PROJECT: ["PROJECT"],
    INTERVENTION: ["INTERVENTION"],
    OFFER: ["OFFER", "INVOICE", "CUSTOMER", "CATALOG"],
    EXPENSE_REPORT: ["PURCHASE_ORDER", "SUPPLIER_INVOICE", "SUPPLIER", "EMAIL_SCAN"],
    INVOICE: ["EXPENSE_REPORT", "BANKING", "ACCOUNTING"],
  };

  /** Mapping activité → types backend */
  private readonly ACTIVITY_TYPE_MAP: Record<string, string[]> = {
    // Vente
    OFFER_OFFER: ["OFFER"],
    OFFER_INVOICE: ["INVOICE"],
    OFFER_CUSTOMER: ["CUSTOMER"],
    OFFER_CATALOG: ["CATALOG"],
    // Dépense
    EXPENSE_REPORT_PURCHASE_ORDER: ["PURCHASE_ORDER"],
    EXPENSE_REPORT_SUPPLIER_INVOICE: ["SUPPLIER_INVOICE"],
    EXPENSE_REPORT_SUPPLIER: ["SUPPLIER"],
    EXPENSE_REPORT_EMAIL_SCAN: ["EMAIL_SCAN"],
    // Finance
    INVOICE_EXPENSE_REPORT: ["EXPENSE_REPORT"],
    INVOICE_BANKING: ["BANKING"],
    INVOICE_ACCOUNTING: ["ACCOUNTING"],
  };

  /** Exposé pour le template */
  readonly taskStatusDone = TaskStatus.DONE;

  /** Contexte objet détecté depuis l'URL (client, offre, chantier…) */
  @Input() objectContext: { object_id: string; object_type: string; object_name?: string } | null = null;

  // ==========================================================================
  // State — Portée (tâches de l'objet de la page / toutes les tâches)
  // ==========================================================================

  /** Portée choisie ; « object » n'a d'effet que sur une page de détail */
  scope: "object" | "all" = "object";

  /** Filtre objet passé au shell, recréé seulement quand l'objet change */
  objectShellFilter: { object_id: string; object_type: string; object_name?: string } | null = null;

  /** Tâches à faire de l'objet de la page (badge de l'onglet objet) */
  objectActiveCount = 0;
  /** Rouge = en retard uniquement (même règle que le bouton de la topbar) */
  objectOverdueCount = 0;

  // ==========================================================================
  // ViewChild
  // ==========================================================================

  @ViewChild("taskListShell") taskListShell?: TaskListShellComponent;

  // ==========================================================================
  // State — Onglets
  // ==========================================================================

  /** Onglet actif */
  activeTab: "all" | "mine" | "done" =
    (localStorage.getItem("eva-drawer-tasks-tab") as "all" | "mine" | "done") || "all";

  /** Nombre de tâches actives (pour badge éventuel) */
  tasksCount = 0;

  // ==========================================================================
  // State — Dialog de détail
  // ==========================================================================

  /** Tâche sélectionnée pour le dialog */
  selectedTask: Task | null = null;

  /** Visibilité du dialog de détail */
  showDetailDialog = false;

  /** Catégories disponibles pour le dialog */
  categories: TaskCategory[] = [];

  /** Employés disponibles pour le dialog */
  employees: Employee[] = [];

  /** Projets chargés pour le filtre "Chantier" */
  projects: Project[] = [];

  // ==========================================================================
  // State — Filtres & Tri
  // ==========================================================================

  /** Filtre actif — toujours un chip sélectionné, défaut : 'PROJECT' (Chantier) */
  activeFilter: string = "ALL";

  /** Types de liens disponibles (count > 0) dans les tâches chargées */
  availableLinkTypes: Array<{ type: string; label: string; icon: string; count: number }> = [];

  /** Collaborateur sélectionné dans le filtre */
  selectedCollaboratorId: string | null = null;

  /** Projet sélectionné dans le second filtre (chip PROJECT) */
  selectedProjectId: string | null = null;

  /** Valeur d'activité sélectionnée dans le second filtre (chips OFFER, EXPENSE_REPORT, INVOICE) */
  selectedActivityValue: string | null = null;

  /** Tri actif */
  sortBy: "manual" | "date" | "urgency" | "title" = "manual";

  /** Menu tri ouvert/fermé */
  showSortMenu = false;

  /** Menu du filtre collaborateur ouvert/fermé */
  showCollaboratorMenu = false;

  /** Recherche textuelle */
  searchTerm = "";
  private searchSubject = new Subject<string>();

  // ==========================================================================
  // Computed — icône selon le type d'objet
  // ==========================================================================

  /** Chips utiles seulement : « Toutes », celles qui ont des tâches et la chip active */
  get visibleLinkTypes(): Array<{ type: string; label: string; icon: string; count: number }> {
    return this.availableLinkTypes.filter((t) => t.type === "ALL" || t.count > 0 || t.type === this.activeFilter);
  }

  /** Convertit activeFilter en tableau de types backend pour le shell */
  get linkTypeFilterForShell(): string[] | null {
    if (this.activeFilter === "ALL") {
      return null;
    }
    if (this.activeFilter === "NONE") {
      return ["NONE"];
    }
    if (this.selectedActivityValue) {
      return this.ACTIVITY_TYPE_MAP[this.selectedActivityValue] ?? [this.activeFilter];
    }
    return this.CHIP_GROUPS[this.activeFilter] ?? [this.activeFilter];
  }

  /** Options collaborateur pour le select */
  get collaboratorOptions(): SelectOption[] {
    return this.employees.map((e) => ({
      label: `${e.first_name} ${e.last_name}`.trim(),
      value: e.employee_id,
    }));
  }

  /** Options projet pour le select "Chantier" */
  get projectOptions(): SelectOption[] {
    return this.projects.map((p) => ({
      label: p.name,
      value: p.project_id,
    }));
  }

  /** Options du second filtre selon le chip actif */
  get secondFilterOptions(): SelectOption[] {
    if (this.activeFilter === "PROJECT") {
      return this.projectOptions;
    }
    if (this.activeFilter === "OFFER") {
      return [
        { label: "Offres", value: "OFFER_OFFER" },
        { label: "Factures clients", value: "OFFER_INVOICE" },
        { label: "Clients & prospects", value: "OFFER_CUSTOMER" },
        { label: "Catalogues", value: "OFFER_CATALOG" },
      ];
    }
    if (this.activeFilter === "EXPENSE_REPORT") {
      return [
        { label: "Bons de commande", value: "EXPENSE_REPORT_PURCHASE_ORDER" },
        { label: "Factures d'achat", value: "EXPENSE_REPORT_SUPPLIER_INVOICE" },
        { label: "Fournisseurs", value: "EXPENSE_REPORT_SUPPLIER" },
        { label: "Scan emails", value: "EXPENSE_REPORT_EMAIL_SCAN" },
      ];
    }
    if (this.activeFilter === "INVOICE") {
      return [
        { label: "Notes de frais", value: "INVOICE_EXPENSE_REPORT" },
        { label: "Banking", value: "INVOICE_BANKING" },
        { label: "Comptabilité", value: "INVOICE_ACCOUNTING" },
      ];
    }
    return [];
  }

  /** Vrai si le chip actif possède un second filtre */
  get hasSecondFilter(): boolean {
    return ["PROJECT", "OFFER", "EXPENSE_REPORT", "INVOICE"].includes(this.activeFilter);
  }

  /** Placeholder du second filtre */
  get secondFilterPlaceholder(): string {
    return this.activeFilter === "PROJECT" ? "Tous les chantiers" : "Toutes les activités";
  }

  /** Collaborateur à passer au shell */
  get collaboratorFilterForShell(): string | null {
    // Portée objet : « Mes tâches » = tâches de l'objet qui me sont assignées
    if (this.isObjectScope && this.activeTab === "mine") {
      return this.evaCompanyService.getCurrentEvaEmployee()?.employee_id ?? null;
    }
    return this.selectedCollaboratorId;
  }

  /** Projet à passer au shell (uniquement si chip PROJECT) */
  get projectIdFilterForShell(): string | null {
    return this.activeFilter === "PROJECT" ? this.selectedProjectId : null;
  }

  /** Vrai sur une page de détail (offre, chantier, client…) */
  get hasObjectScope(): boolean {
    return !!this.objectContext?.object_id;
  }

  /** Le filtre collaborateur n'a pas de sens sur « Mes tâches » ni « Terminées » */
  get showCollaboratorFilter(): boolean {
    return this.activeTab !== "mine" && this.activeTab !== "done";
  }

  get collaboratorLabel(): string {
    return this.collaboratorOptions.find((o) => o.value === this.selectedCollaboratorId)?.label || "Tous";
  }

  get objectCountTooltip(): string {
    const active = this.objectActiveCount;
    if (this.objectOverdueCount)
      return `${this.objectOverdueCount} en retard sur ${active} tâche${active > 1 ? "s" : ""} à faire`;
    return active ? `${active} tâche${active > 1 ? "s" : ""} à faire` : "Aucune tâche à faire";
  }

  get isObjectScope(): boolean {
    return this.hasObjectScope && this.scope === "object";
  }

  /** Libellé de l'onglet objet : « Offre n°2026-0412 » quand la page l'a fourni */
  get objectScopeLabel(): string {
    // Même libellé que le bandeau des notes (« Offre n°O26179 ») quand la page l'a fourni
    return this.objectContext?.object_name?.trim() || formatObjectScopeLabel(this.objectContext?.object_type, "");
  }

  get sortLabel(): string {
    switch (this.sortBy) {
      case "manual":
        return "Manuel";
      case "urgency":
        return "Urgence";
      case "title":
        return "Titre";
      default:
        return "Échéance";
    }
  }

  // ==========================================================================
  // Computed — mapping onglet → viewMode shell
  // ==========================================================================

  /** Convertit l'onglet actif en viewMode attendu par task-list-shell */
  get shellViewMode(): "all" | "mine" | "completed" {
    switch (this.activeTab) {
      case "done":
        return "completed";
      case "mine":
        return "mine";
      case "all":
      default:
        return "all";
    }
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnInit(): void {
    this.loadCategories();
    this.loadEmployees();
    this.loadProjects();
    this.setupSearch();

    // Bouton « Tâches » d'une fiche : galet déjà ouvert, on revient sur l'onglet de l'objet
    this.noteContextService.drawerTasksOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setScope("object"));

    // Badge de l'onglet objet : toute création / modification / suppression le rafraîchit
    this.taskService.tasksChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadObjectCount());
  }

  private setupSearch(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => {
        this.searchTerm = term;
        this.taskListShell?.refresh();
      });
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["objectContext"]) {
      const ctx = changes["objectContext"].currentValue;
      this.updateObjectShellFilter(ctx);
      // « Toutes les tâches » veut dire toutes : aucun filtre préactivé selon la page (EP-1120),
      // les tâches de l'objet ont leur propre onglet
      this.activeFilter = "ALL";
      this.selectedProjectId = null;
      this.selectedActivityValue = null;
      this.cdr.markForCheck();
    }
  }

  /** Le shell recharge à chaque nouvelle référence : on n'en crée une que si l'objet change */
  private updateObjectShellFilter(ctx: { object_id: string; object_type: string; object_name?: string } | null): void {
    if (!ctx?.object_id || !ctx?.object_type) {
      this.objectShellFilter = null;
      this.objectActiveCount = 0;
      this.objectOverdueCount = 0;
      return;
    }
    const sameObject =
      this.objectShellFilter?.object_id === ctx.object_id &&
      this.objectShellFilter?.object_type === ctx.object_type.toUpperCase();
    if (sameObject) {
      if (this.objectShellFilter && ctx.object_name) this.objectShellFilter.object_name = ctx.object_name;
      return;
    }
    this.objectShellFilter = {
      object_id: ctx.object_id,
      object_type: ctx.object_type.toUpperCase(),
      object_name: ctx.object_name,
    };
    this.loadObjectCount();
  }

  /** Badge de l'onglet objet, y compris quand l'utilisateur est sur « Toutes les tâches » */
  private loadObjectCount(): void {
    const filter = this.objectShellFilter;
    if (!filter) return;
    this.taskService
      .getTasksByObject(filter.object_type, filter.object_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tasks) => {
        if (this.objectShellFilter?.object_id === filter.object_id) this.onObjectTasksLoaded(tasks);
      });
  }

  onObjectTasksLoaded(tasks: Task[]): void {
    const active = tasks.filter((t) => t.status === TaskStatus.TODO || t.status === TaskStatus.IN_PROGRESS);
    this.objectActiveCount = active.length;
    this.objectOverdueCount = active.filter((t) => t.isOverdue()).length;
    this.cdr.markForCheck();
  }

  setScope(scope: "object" | "all"): void {
    this.scope = scope;
    this.cdr.markForCheck();
  }

  private loadCategories(): void {
    this.taskCategoryService
      .getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories) => {
        this.categories = categories;
        this.cdr.markForCheck();
      });
  }

  private loadEmployees(): void {
    this.employeesService
      .getAllEnabledEmployees()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((employees) => {
        this.employees = employees;
        this.cdr.markForCheck();
      });
  }

  private loadProjects(): void {
    this.projectService
      .getAllProjects()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projects) => {
        this.projects = projects;
        this.cdr.markForCheck();
      });
  }

  // ==========================================================================
  // Tab switching
  // ==========================================================================

  onTabChange(tab: "all" | "mine" | "done"): void {
    this.activeTab = tab;
    localStorage.setItem("eva-drawer-tasks-tab", this.activeTab);
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Filtres & Tri
  // ==========================================================================

  setFilter(filter: string): void {
    this.activeFilter = filter;
    this.selectedProjectId = null;
    this.selectedActivityValue = null;
    this.cdr.markForCheck();
  }

  /** Gestion du second filtre (projet ou activité) */
  onSecondFilterChange(value: string | null): void {
    if (this.activeFilter === "PROJECT") {
      this.selectedProjectId = value;
    } else {
      this.selectedActivityValue = value;
    }
    this.cdr.markForCheck();
  }

  /** Reçoit les tâches brutes du shell pour calculer les chips de filtre */
  onRawTasksLoaded(tasks: Task[]): void {
    const chipDefs: Array<{ type: string; label: string; icon: string }> = [
      { type: "ALL", label: "Toutes", icon: "pi-list" },
      { type: "NONE", label: "Autre", icon: "pi-tag" },
      { type: "PROJECT", label: "Chantier", icon: "pi-hammer" },
      { type: "INTERVENTION", label: "Intervention", icon: "pi-wrench" },
      { type: "OFFER", label: "Vente", icon: "pi-file" },
      { type: "EXPENSE_REPORT", label: "Dépense", icon: "pi-wallet" },
      { type: "INVOICE", label: "Finance", icon: "pi-receipt" },
    ];

    // Flat de tous les types connus (pour détecter les tâches "Autre")
    const allKnownTypes = new Set(Object.values(this.CHIP_GROUPS).flat());

    // Comptage par chip : une tâche compte pour un chip si elle a au moins un lien dans son groupe
    const chipCounts = new Map<string, number>();
    let noneCount = 0;

    for (const task of tasks) {
      const links = task.objects_links ?? [];
      const taskTypes = new Set(links.map((l) => l.object_type?.toUpperCase()).filter(Boolean));

      const hasAnyKnownLink = [...taskTypes].some((t) => allKnownTypes.has(t));
      if (!hasAnyKnownLink) {
        noneCount++;
      } else {
        for (const [chipType, groupTypes] of Object.entries(this.CHIP_GROUPS)) {
          const hasLink = groupTypes.some((gt) => taskTypes.has(gt));
          if (hasLink) {
            chipCounts.set(chipType, (chipCounts.get(chipType) ?? 0) + 1);
          }
        }
      }
    }

    this.availableLinkTypes = chipDefs.map(({ type, label, icon }) => ({
      type,
      label,
      icon,
      count: type === "ALL" ? tasks.length : type === "NONE" ? noneCount : (chipCounts.get(type) ?? 0),
    }));

    this.cdr.markForCheck();
  }

  setSortBy(sort: "manual" | "date" | "urgency" | "title"): void {
    this.sortBy = sort;
    this.showSortMenu = false;
    this.cdr.markForCheck();
  }

  toggleSortMenu(): void {
    this.showSortMenu = !this.showSortMenu;
    this.showCollaboratorMenu = false;
    this.cdr.markForCheck();
  }

  toggleCollaboratorMenu(): void {
    this.showCollaboratorMenu = !this.showCollaboratorMenu;
    this.showSortMenu = false;
    this.cdr.markForCheck();
  }

  onCollaboratorChange(id: string | null): void {
    this.selectedCollaboratorId = id;
    this.showCollaboratorMenu = false;
    this.cdr.markForCheck();
  }

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showSortMenu || this.showCollaboratorMenu,
    (event) => this.onDocumentClick(event),
  );

  onDocumentClick(event: Event): void {
    // Hors du bouton et de son menu (la liste d'eva-select vit dans le body)
    if (!(event.target as HTMLElement | null)?.closest(".drawer-tasks__sort, .eva-select__dropdown")) {
      if (this.showSortMenu || this.showCollaboratorMenu) {
        this.showSortMenu = false;
        this.showCollaboratorMenu = false;
        this.cdr.markForCheck();
      }
    }
  }

  // ==========================================================================
  // Events du shell
  // ==========================================================================

  /** Clic sur une tâche : ouvre le dialog de détail sans naviguer */
  onTaskClicked(task: Task): void {
    this.selectedTask = task;
    this.showDetailDialog = true;
    this.cdr.markForCheck();
  }

  onTasksCountChange(count: number): void {
    this.tasksCount = count;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Events du dialog
  // ==========================================================================

  onDialogVisibleChange(visible: boolean): void {
    this.showDetailDialog = visible;
    if (!visible) {
      this.selectedTask = null;
    }
    this.cdr.markForCheck();
  }

  onTaskUpdated(task: Task): void {
    // Rafraîchir la liste après mise à jour
    this.taskListShell?.refresh();
    this.cdr.markForCheck();
  }

  onTaskDeleted(taskId: string): void {
    this.showDetailDialog = false;
    this.selectedTask = null;
    // Rafraîchir la liste après suppression
    this.taskListShell?.refresh();
    this.cdr.markForCheck();
  }
}
